import { Column } from "@/constants/pipeline";
import { ArrowDown, ArrowUp, ArrowUpDown } from "lucide-react";
import * as React from "react";
import { AccordionBase } from "./AccordionBase";

type AccordionProps = {
  title: string;
  id: string;
  count: number;
  columns: Column[];
  stats?: {
    offToday?: number;
    handoversToday?: number;
    offTomorrow?: number;
    handoversTomorrow?: number;
  };
  children: React.ReactNode;
  defaultExpanded?: boolean;
  classes?: {
    wrapper?: string;
    counter?: string;
  };
  expanded?: boolean;
  onChange?: (expanded: boolean) => void;
  sortBy?: string;
  sortOrder?: "asc" | "desc";
  onSort: (sortKey: string) => void;
};

export const Accordion: React.FC<AccordionProps> = ({
  title,
  id,
  count,
  columns,
  stats,
  children,
  defaultExpanded,
  classes,
  expanded,
  onChange,
  sortBy,
  sortOrder,
  onSort,
}) => {
  return (
    <AccordionBase
      title={title}
      count={count}
      panelId={id}
      stats={stats}
      defaultExpanded={defaultExpanded}
      className={classes?.wrapper}
      classes={classes}
      expanded={expanded}
      onChange={onChange}
    >
      <div className="w-full overflow-x-auto">
        <table className="w-full border-collapse">
          <colgroup className="hidden md:table-column-group">
            {Array.from({ length: 21 }, (_, i) => (
              <col key={i} />
            ))}
          </colgroup>
          <thead>
            <tr className="h-10">
              {columns.map((column, index) => {
                const alignClass =
                  column.align === "center"
                    ? "text-center [&>*]:mx-auto"
                    : column.align === "right"
                      ? "text-right"
                      : "text-left";
                const paddingClass = column.reducePadding ? "px-3 md:px-0 py-3" : "px-3 py-3";
                return (
                  <th
                    key={column.key || index}
                    colSpan={column.colspan}
                    className={`${paddingClass} align-middle text-xs font-bold text-slate-700 ${alignClass}`}
                  >
                    {column.sortable !== false && column.key ? (
                      <div
                        onClick={() => onSort(column.key)}
                        className="relative w-fit cursor-pointer select-none hover:opacity-70"
                      >
                        {sortBy === column.key && sortOrder === "asc" && (
                          <ArrowUp
                            className={
                              "animate-in slide-in-from-bottom-1 absolute top-1/2 -right-3.5 h-3 w-3 -translate-y-1/2 text-slate-700 duration-500"
                            }
                          />
                        )}
                        {column.label}
                        {sortBy === column.key && sortOrder === "desc" ? (
                          <ArrowDown
                            className={
                              "animate-in slide-in-from-top-1 absolute top-1/2 -right-3.5 h-3 w-3 -translate-y-1/2 text-slate-700 duration-500"
                            }
                          />
                        ) : sortBy !== column.key && (
                          <ArrowUpDown
                            className="absolute top-1/2 -right-3.5 h-3 w-3 -translate-y-1/2 text-slate-400"
                          />
                        )}
                      </div>
                    ) : (
                      column.label
                    )}
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody className="rounded-md border border-solid border-slate-200 bg-white">{children}</tbody>
        </table>
      </div>
    </AccordionBase>
  );
};
